// Phase 83v — Apply SEO meta tags (title / description / og_image) to <head>
// at runtime per route.
//
// Reads `branding.seo.routes[path]` configured in Admin Branding (Phase 83r) and
// injects/updates the relevant <title> + <meta> tags. When the user navigates
// away, falls back to a sensible default (root `/` route or static index.html).
//
// Route matching:
//   1. Exact path match wins (e.g. "/login")
//   2. Prefix match for path patterns ending without trailing slash
//      (e.g. config "/athlete/tournaments" matches both "/athlete/tournaments"
//      and "/athlete/tournaments/{id}")
//   3. Fallback "/" or static index.html values
//
// Usage: mount once at root (App.jsx).  Auto-listens to location changes.
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useBranding } from "../hooks/useBranding";

const DEFAULT_TITLE = "PickLeague — Giải đấu Pickleball";
const DEFAULT_DESC =
  "PickLeague — nền tảng quản lý giải đấu pickleball cho BTC và vận động viên Việt Nam.";

function findRouteConfig(routes, pathname) {
  if (!routes || typeof routes !== "object") return null;
  // 1) exact match
  if (routes[pathname]) return routes[pathname];
  // 2) prefix match — longest prefix wins (so `/athlete/tournaments` wins over `/`)
  const candidates = Object.keys(routes).filter((p) => {
    if (p === "/") return false;
    if (pathname === p) return true;
    return pathname.startsWith(`${p}/`);
  });
  if (candidates.length === 0) return routes["/"] || null;
  candidates.sort((a, b) => b.length - a.length);
  return routes[candidates[0]] || routes["/"] || null;
}

function setMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!content) {
    // Don't strip a static meta provided in index.html — only clear the dynamic one.
    if (el && el.dataset.seoSource === "runtime") {
      el.parentElement?.removeChild(el);
    }
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.dataset.seoSource = "runtime";
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function SeoHead() {
  const branding = useBranding();
  const location = useLocation();
  const pathname = location.pathname || "/";

  const config = useMemo(() => findRouteConfig(branding?.seo?.routes, pathname), [branding, pathname]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const title = (config?.title && config.title.trim()) || DEFAULT_TITLE;
    const desc = (config?.description && config.description.trim()) || DEFAULT_DESC;
    const ogImage = config?.og_image || branding?.logos?.full || branding?.logos?.mark || null;

    // <title>
    const prevTitle = document.title;
    document.title = title;

    // <meta name="description">
    setMeta("description", desc);

    // Open Graph
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", typeof window !== "undefined" ? window.location.href : "", "property");
    if (ogImage) setMeta("og:image", ogImage, "property");
    else setMeta("og:image", null, "property");

    // Twitter cards
    setMeta("twitter:card", ogImage ? "summary_large_image" : "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    if (ogImage) setMeta("twitter:image", ogImage);
    else setMeta("twitter:image", null);

    return () => {
      // Restore previous title on unmount (defensive — outer page will reset anyway)
      document.title = prevTitle;
    };
  }, [config, branding, pathname]);

  return null;
}
